<template>
  <div class="elevation-profile-container" :class="{ collapsed: isCollapsed, expanded: isExpanded }">
    <div class="profile-header">
      <button class="collapse-btn" @click="toggleCollapse" :title="isCollapsed ? 'Show elevation profile' : 'Hide elevation profile'">
        <span class="material-icons">{{ isCollapsed ? 'expand_less' : 'expand_more' }}</span>
      </button>
      <h3 class="profile-title d-none d-md-block">
        {{ profileTitle }}
        <small v-if="viewSubtitle" class="view-subtitle">{{ viewSubtitle }}</small>
      </h3>
      <div class="profile-stats">
        <span class="stat-item">
          <span class="material-icons">straighten</span>
          {{ formatDistance(totalDistance) }}
        </span>
        <span class="stat-item">
          <span class="material-icons">trending_up</span>
          {{ formatElevation(totalElevationGain) }}
        </span>
        <span class="stat-item">
          <span class="material-icons">schedule</span>
          {{ formatDuration(totalDuration) }}
        </span>
      </div>
      <button class="expand-btn" @click="toggleExpand" :title="isExpanded ? 'Minimize' : 'Maximize'">
        <span class="material-icons" style="font-size:20px">{{ isExpanded ? 'fullscreen_exit' : 'fullscreen' }}</span>
      </button>
    </div>
    
    <div class="profile-content" v-show="!isCollapsed">
      <div class="chart-container" ref="chartContainer">
        <canvas ref="chartCanvas"></canvas>
      </div>
      <div v-if="loading" class="loading-overlay">
        <div class="spinner-border text-light" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, nextTick, markRaw } from 'vue';
import { Chart, registerables } from 'chart.js';
import mapboxgl from 'mapbox-gl';
import { distance as turfDistance } from '@turf/turf';
import { MeasurementPrefUtility } from '../../utils/helpers/MeasurementPref';

Chart.register(...registerables);

// Keep a global reference to the marker
let elevationMarker: mapboxgl.Marker | null = null;

export default defineComponent({
  name: 'HikeMapElevationProfile',
  props: {
    geojson: {
      type: Object,
      default: null
    },
    activeView: {
      type: Object,
      default: () => ({ type: 'full' })
    },
    plannedTrails: {
      type: Array,
      default: () => []
    },
    hikeDays: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isCollapsed: false,
      isExpanded: false,
      loading: false,
      chartInstance: null as Chart<'line', number[], string> | null,
      isChartUpdating: false,
      pendingChartUpdate: false,
      currentFeatures: [] as any[],
      totalDistance: 0,
      totalElevationGain: 0,
      totalDuration: 0
    };
  },
  computed: {
    profileTitle(): string {
      if (this.activeView.type === 'day') {
        const dayInfo = this.hikeDays.find((d: any) => d.dayNumber === this.activeView.dayNumber);
        return dayInfo?.name || `Day ${this.activeView.dayNumber}`;
      } else if (this.activeView.type === 'route') {
        const trail = this.getTrailById(this.activeView.trailId);
        return trail?.title || 'Route';
      }
      return 'Elevation';
    },
    
    viewSubtitle(): string {
      if (this.activeView.type === 'day') {
        const dayInfo = this.hikeDays.find((d: any) => d.dayNumber === this.activeView.dayNumber);
        return dayInfo?.description || '';
      }
      return '';
    },
    
    // Build an ordering map from ptv2PlannedTrailId -> sort index using the
    // plannedTrails prop (same data source as the left panel, ordered by day then position).
    trailOrderMap(): Map<string, number> {
      const map = new Map<string, number>();
      [...(this.plannedTrails as any[])]
        .sort((a, b) => ((a.day ?? 0) - (b.day ?? 0)) || ((a.position ?? 0) - (b.position ?? 0)))
        .forEach((t, idx) => {
          const id = t.ptv2PlannedTrail?.id ?? t.id;
          if (id != null) map.set(String(id), idx);
        });
      return map;
    },

    filteredFeatures(): any[] {
      if (!this.geojson?.features) return [];

      let features = this.geojson.features;

      if (this.activeView.type === 'day') {
        // For day view: show only 'route' type within that day (exclude alt, spur, transport)
        features = features.filter((f: any) =>
          f.properties?.hikeDayNumber === this.activeView.dayNumber &&
          f.properties?.routeStyle === 'route'
        );
      } else if (this.activeView.type === 'route') {
        // For individual route: show just that route (any type including alt, spur, transport)
        features = features.filter((f: any) =>
          f.properties?.ptv2PlannedTrailId != null &&
          String(f.properties.ptv2PlannedTrailId) === String(this.activeView.trailId)
        );
      } else {
        // For full view: show only 'route' type (exclude alt, spur, transport)
        features = features.filter((f: any) =>
          f.properties?.routeStyle === 'route'
        );
      }

      // Sort features to match the left panel's day/position sequence.
      // The trailOrderMap is built from plannedTrails (hikesPlannedTrails join table data)
      // which is the authoritative ordering — the same data the left panel uses.
      if (this.trailOrderMap.size > 0) {
        features = [...features].sort((a: any, b: any) => {
          const aIdx = this.trailOrderMap.get(String(a.properties?.ptv2PlannedTrailId)) ?? 9999;
          const bIdx = this.trailOrderMap.get(String(b.properties?.ptv2PlannedTrailId)) ?? 9999;
          return aIdx - bIdx;
        });
      }

      return features;
    },
    
    multiLineStrings(): any[] {
      return this.filteredFeatures.filter(f => f.geometry?.type === 'MultiLineString');
    },
    
    chartData(): any {
      const multiLineStrings = this.multiLineStrings;
      if (!multiLineStrings.length) return null;
      
      const measurementUtils = MeasurementPrefUtility.preference();
      const allCoordinates: number[][] = [];
      
      // Extract all coordinates from multilinestrings
      multiLineStrings.forEach(feature => {
        if (feature.geometry?.coordinates) {
          feature.geometry.coordinates.forEach((lineString: number[][]) => {
            allCoordinates.push(...lineString);
          });
        }
      });
      
      if (!allCoordinates.length) return null;
      
      // Build distance and elevation arrays
      const distances: number[] = [];
      // {x, y} points: x is real cumulative distance (user's big unit), y is
      // elevation. Plotting on a linear distance axis (rather than a category
      // axis spaced by array index) prevents long straight segments — few,
      // far-apart points — from collapsing into vertical "steps".
      const points: { x: number; y: number }[] = [];
      const labels: string[] = [];
      const lats: number[] = [];
      const lons: number[] = [];
      let totalDistance = 0;
      let minElevation = Infinity;
      let maxElevation = -Infinity;

      allCoordinates.forEach((coord, index) => {
        if (index > 0) {
          const segmentDistance = turfDistance(
            allCoordinates[index - 1].slice(0, 2),
            coord.slice(0, 2),
            { units: 'meters' }
          );
          totalDistance += segmentDistance;
        }

        const elevation = coord[2] || 0; // Use altitude if available
        distances.push(totalDistance);
        points.push({ x: measurementUtils.bigUnitValue(totalDistance / 1000), y: measurementUtils.smallUnitValue(elevation) });
        labels.push(measurementUtils.bigUnitFormatter(totalDistance / 1000, false));
        lons.push(coord[0]);
        lats.push(coord[1]);
        
        minElevation = Math.min(minElevation, elevation);
        maxElevation = Math.max(maxElevation, elevation);
      });
      
      // Calculate total elevation gain
      let elevationGain = 0;
      for (let i = 1; i < allCoordinates.length; i++) {
        const elevDiff = (allCoordinates[i][2] || 0) - (allCoordinates[i - 1][2] || 0);
        if (elevDiff > 0) {
          elevationGain += elevDiff;
        }
      }
      
      // Update totals
      this.totalDistance = totalDistance;
      this.totalElevationGain = elevationGain;
      this.updateDuration();
      
      // Simplify data points for performance (max 200 points)
      const maxPoints = 200;
      const step = Math.max(1, Math.floor(points.length / maxPoints));
      const simplifiedData = {
        labels: labels.filter((_, i) => i % step === 0 || i === labels.length - 1),
        datasets: [{
          label: measurementUtils.smallUnitLabel,
          data: points.filter((_, i) => i % step === 0 || i === points.length - 1),
          borderColor: '#FFDF46',
          backgroundColor: 'rgba(255, 223, 70, 0.2)',
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          tension: 0.2,
          fill: true
        }],
        lat: lats.filter((_, i) => i % step === 0 || i === lats.length - 1),
        lon: lons.filter((_, i) => i % step === 0 || i === lons.length - 1)
      };
      
      return simplifiedData;
    }
  },
  watch: {
    activeView: {
      deep: true,
      handler() {
        this.updateChart();
      }
    },
    geojson: {
      deep: true,
      handler() {
        this.updateChart();
      }
    }
  },
  methods: {
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
      if (!this.isCollapsed) {
        this.$nextTick(() => {
          this.updateChart();
        });
      }
    },
    
    toggleExpand() {
      this.isExpanded = !this.isExpanded;
      if (this.isExpanded) {
        this.isCollapsed = false;
      }
      this.$nextTick(() => {
        this.updateChart();
      });
    },
    
    getTrailById(trailId: string | number): any {
      for (const trail of this.plannedTrails) {
        if ((trail as any).ptv2PlannedTrail?.id === trailId) {
          return (trail as any).ptv2PlannedTrail;
        } else if ((trail as any).id === trailId) {
          return trail;
        }
      }
      return null;
    },
    
    updateDuration() {
      // Calculate duration based on active view
      if (this.activeView.type === 'route') {
        // For individual route: show duration regardless of type
        const trail = this.getTrailById(this.activeView.trailId);
        this.totalDuration = (trail?.calculatedDurationMinutes || 0) * 60;
      } else if (this.activeView.type === 'day') {
        // Sum durations for only 'route' type trails in the day
        let duration = 0;
        this.plannedTrails.forEach((trail: any) => {
          if (trail.day === this.activeView.dayNumber) {
            const plannedTrail = trail.ptv2PlannedTrail || trail;
            // Include only main route segments (exclude alt, spur, transport)
            if (plannedTrail.routeStyle === 'route') {
              duration += (plannedTrail.calculatedDurationMinutes || 0);
            }
          }
        });
        this.totalDuration = duration * 60;
      } else {
        // Full trail - sum only 'route' type durations
        let duration = 0;
        this.plannedTrails.forEach((trail: any) => {
          const plannedTrail = trail.ptv2PlannedTrail || trail;
          // Include only main route segments (exclude alt, spur, transport)
          if (plannedTrail.routeStyle === 'route') {
            duration += (plannedTrail.calculatedDurationMinutes || 0);
          }
        });
        this.totalDuration = duration * 60;
      }
    },
    
    formatDistance(meters: number): string {
      const measurementPref = MeasurementPrefUtility.preference();
      return measurementPref.bigUnitFormatter(meters / 1000, true);
    },
    
    formatElevation(meters: number): string {
      const measurementPref = MeasurementPrefUtility.preference();
      return measurementPref.smallUnitFormatter(meters, false);
    },
    
    formatDuration(seconds: number): string {
      const minutes = seconds / 60;
      if (!minutes) return '0h';
      const hours = Math.floor(minutes / 60);
      const mins = Math.floor(minutes % 60);
      if (hours === 0) return `${mins}m`;
      if (mins === 0) return `${hours}h`;
      return `${hours}h ${mins}m`;
    },
    
    createElevationMarker(): mapboxgl.Marker {
      const el = document.createElement('div');
      el.className = 'elevation-marker';
      return new mapboxgl.Marker(el);
    },

    removeElevationMarker() {
      if (elevationMarker) {
        elevationMarker.remove();
        elevationMarker = null;
      }
    },
    
    updateChart() {
      if (this.isChartUpdating) {
        this.pendingChartUpdate = true;
        return;
      }

      this.isChartUpdating = true;
      nextTick(() => {
        if (this.$refs.chartCanvas && this.chartData) {
          this.drawChart();
        } else {
          this.destroyChartInstance();
          this.finishChartUpdate();
        }
      });
    },
    
    drawChart() {
      const chartData = this.chartData;
      if (!chartData) {
        this.destroyChartInstance();
        this.finishChartUpdate();
        return;
      }

      const canvas = this.$refs.chartCanvas as HTMLCanvasElement | undefined;
      const ctx = canvas?.getContext('2d');
      if (!ctx) {
        this.finishChartUpdate();
        return;
      }

      // Pin the linear x-axis to the route's actual end distance, otherwise
      // Chart.js rounds the max up to a "nice" value and leaves empty profile
      // at the end (e.g. axis runs to 6 km for a 5.27 km route).
      const seriesData = chartData.datasets?.[0]?.data as Array<{ x: number; y: number }> | undefined;
      const lastX = seriesData && seriesData.length ? seriesData[seriesData.length - 1].x : undefined;

      const buildChartData = () => ({
        labels: chartData.labels.slice(),
        datasets: chartData.datasets.map(dataset => ({
          ...dataset,
          data: dataset.data.slice(),
        })),
        lat: chartData.lat.slice(),
        lon: chartData.lon.slice(),
      });

      if (!this.chartInstance) {
        const verticalLinePlugin = {
          id: 'verticalHoverLine',
          afterDraw: (chart: Chart) => {
            const activeElements = chart.getActiveElements();
            if (activeElements && activeElements.length > 0) {
              const activePoint = activeElements[0];
              const ctx = chart.ctx;
              const x = activePoint.element.x;
              const y = chart.chartArea.top;
              const yBottom = chart.chartArea.bottom;

              ctx.save();
              ctx.beginPath();
              ctx.moveTo(x, y);
              ctx.lineTo(x, yBottom);
              ctx.lineWidth = 1;
              ctx.strokeStyle = 'rgba(255, 223, 70, 0.5)';
              ctx.stroke();
              ctx.restore();
            }
          }
        };

        this.chartInstance = markRaw(new Chart(ctx, {
          type: 'line',
          data: buildChartData(),
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
              mode: 'index',
              intersect: false
            },
            animation: false,
            onHover: (event: Event, activeElements: any[], chart: Chart) => {
              if (event.type === 'mouseout') {
                this.removeElevationMarker();
                return;
              }

              // Remove marker when no active elements (mouse moved away from line)
              if (!activeElements || activeElements.length === 0) {
                this.removeElevationMarker();
                return;
              }

              if (activeElements && activeElements.length > 0) {
                const hoveredIndex = activeElements[0].index;
                const data = chart.data as any;

                if (data.lat && data.lon && hoveredIndex < data.lat.length) {
                  const lat = data.lat[hoveredIndex];
                  const lon = data.lon[hoveredIndex];

                  if (!elevationMarker) {
                    elevationMarker = this.createElevationMarker();
                  }

                  if ((window as any).map && typeof (window as any).map.getCenter === 'function') {
                    try {
                      elevationMarker.setLngLat([lon, lat]).addTo((window as any).map);
                    } catch(error) {
                      console.error("Error adding marker to map:", error);
                      this.removeElevationMarker();
                    }
                  }
                }
              }
            },
            plugins: {
              legend: {
                display: false
              },
              tooltip: {
                mode: 'index',
                intersect: false,
                backgroundColor: 'rgba(40, 42, 50, 0.95)',
                titleColor: '#fff',
                bodyColor: '#fff',
                borderColor: '#FFDF46',
                borderWidth: 1,
                padding: 8,
                displayColors: false,
                callbacks: {
                  title: (tooltipItems) => {
                    const measurementPref = MeasurementPrefUtility.preference();
                    return `Distance: ${tooltipItems[0].parsed.x} ${measurementPref.kilometerSymbol()}`;
                  },
                  label: (context) => {
                    const measurementPref = MeasurementPrefUtility.preference();
                    return `Elevation: ${context.parsed.y}${measurementPref.meterSymbol()}`;
                  }
                }
              }
            },
            scales: {
              x: {
                // Linear distance axis — points carry real cumulative distance as
                // {x, y}, so spacing reflects distance, not array index. See note
                // in buildChartData() on the "stepping" artifact this avoids.
                type: 'linear',
                min: 0,
                max: lastX,
                display: true,
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: { size: 10 },
                  maxTicksLimit: 8,
                  callback: (v: number | string) => `${v} ${MeasurementPrefUtility.preference().kilometerSymbol()}`
                }
              },
              y: {
                display: true,
                beginAtZero: false,
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)',
                  font: { size: 10 }
                }
              }
            }
          },
          plugins: [verticalLinePlugin]
        }));

        // Add direct mouseleave listener to canvas as failsafe
        if (canvas) {
          canvas.addEventListener('mouseleave', this.removeElevationMarker);
        }

        this.finishChartUpdate();
        return;
      }

      try {
        const chart = this.chartInstance;
        chart.stop();

        const updatedData = buildChartData();
        chart.data.labels = updatedData.labels;

        // Update datasets in place to avoid reintroducing reactive proxies
        updatedData.datasets.forEach((incoming, index) => {
          const existing = chart.data.datasets[index];
          if (existing) {
            existing.data = incoming.data.slice();
            existing.label = incoming.label;
            existing.borderColor = incoming.borderColor;
            existing.backgroundColor = incoming.backgroundColor;
            existing.borderWidth = incoming.borderWidth;
            existing.pointRadius = incoming.pointRadius;
            existing.pointHoverRadius = incoming.pointHoverRadius;
            existing.tension = incoming.tension;
            existing.fill = incoming.fill;
          } else {
            chart.data.datasets[index] = {
              ...incoming,
              data: incoming.data.slice(),
            };
          }
        });

        // Remove any extra datasets
        chart.data.datasets.length = updatedData.datasets.length;

        (chart.config.data as any).lat = updatedData.lat;
        (chart.config.data as any).lon = updatedData.lon;
        (chart.data as any).lat = updatedData.lat;
        (chart.data as any).lon = updatedData.lon;

        // Keep the x-axis pinned to the (possibly changed) end distance.
        const xScale = (chart.options.scales as any)?.x;
        if (xScale) xScale.max = lastX;

        chart.update('none');
      } catch (error) {
        console.error('Error updating elevation chart', error);
        this.destroyChartInstance();
      } finally {
        this.finishChartUpdate();
      }
    },

    destroyChartInstance() {
      if (!this.chartInstance) {
        return;
      }

      try {
        this.chartInstance.stop();
      } catch (error) {
        console.warn('Error stopping chart before destroy', error);
      }

      // Remove event listener from canvas
      const canvas = this.$refs.chartCanvas as HTMLCanvasElement | undefined;
      if (canvas) {
        canvas.removeEventListener('mouseleave', this.removeElevationMarker);
      }

      this.chartInstance.destroy();
      this.chartInstance = null;
    },

    finishChartUpdate() {
      this.isChartUpdating = false;
      if (this.pendingChartUpdate) {
        this.pendingChartUpdate = false;
        this.updateChart();
      }
    }
  },
  mounted() {
    this.updateChart();
  },
  beforeUnmount() {
    this.destroyChartInstance();
    this.removeElevationMarker();
  }
});
</script>

<style scoped>
.elevation-profile-container {
  position: absolute;
  bottom: 40px;
  left: 20px;
  right: 20px;
  margin: auto;
  max-width: 630px;
  height: 180px;
  background: rgba(40, 42, 50, 0.95);
  backdrop-filter: blur(10px);
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.4);
  border-radius: 12px;
  z-index: 999;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  color: #fff;
}

.elevation-profile-container.collapsed {
  height: 48px;
}

.elevation-profile-container.expanded {
  height: 400px;
  max-width: calc(100% - 40px);
}

.profile-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(30, 32, 38, 0.8);
  border-radius: 12px 12px 0 0;
}

.profile-title {
  flex: 0 0 auto;
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.view-subtitle {
  font-size: 10px;
  font-weight: normal;
  color: rgba(255, 255, 255, 0.6);
  font-style: italic;
}

.profile-stats {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 12px;
}

.stat-item .material-icons {
  font-size: 16px;
  color: #FFDF46;
}

.collapse-btn,
.expand-btn {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.collapse-btn:hover,
.expand-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.profile-content {
  flex: 1;
  position: relative;
  padding: 6px;
  overflow: hidden;
}

.chart-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 0 0 12px 12px;
}

/* Mobile responsive */
@media (max-width: 768px) {
    .elevation-profile-container[data-v-f31e0fc7] {
      left: 10px;
      right: 10px;
      /* bottom: 10px; */
      height: 150px;
    }
  
  .elevation-profile-container.expanded {
    height: 300px;
  }
  
  .profile-stats {
    gap: 22px;
  }
  
  .stat-item {
    font-size: 12px;
  }
  
  .stat-item .material-icons {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .profile-stats {
    gap: 8px;
  }
  
  .stat-item span:not(.material-icons) {
    display: none;
  }
  
  .stat-item .material-icons {
    margin-right: 0;
  }
}

/* Elevation Marker Styling */
:global(.elevation-marker) {
  display: block;
  border: 2px solid #fff;
  background-color: #FFDF46;
  border-radius: 50%;
  width: 12px;
  height: 12px;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}
</style>
